import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { DataType, DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DatePart, EncFilterField, Encoding, FilterType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { DialogHeaderComponent } from '@mode/shared/ui';
import { escapeAttrName } from '@mode/shared/util-js';
import { CompiledField, CompiledFormula, FormulaOperations } from '@mode/shared/util-switch';
import { BehaviorSubject } from 'rxjs';
import { relativeDateFilterSourcedByFilter } from '../relative-date-panel/helpers';
import { FilterConfigurationComponent } from './filter-configuration/filter-configuration.component';
import { convertContinuousDateToRelativeDate, makeRelativeDateFilterField, shortId } from './filter-modal-helpers';
import { FilterTypeDetail, FilterTypeSelectorComponent } from './filter-type-selector/filter-type-selector.component';

export interface FilterModalData {
  formula: CompiledFormula;
  initialFilter: EncFilterField | undefined;
  view: FlamingoTypes.ModeView;
  encoding: Encoding;
  queryRunToken: string;
  applyFilter: (filter: EncFilterField, field: CompiledField) => void;
  onCancel: () => void;
}

export enum FilterStep {
  SelectType,
  MultiValues,
  TopByField,
  Range,
  RelativeDateRange,
}

/**
 * This is the main top-level component for the filter modal.
 *
 * The primary responsibility is to instantiate the correct
 * filter configuration. There are two possibilities when
 * launching the filter modal
 *  1. The exact filter type is known either because we are
 *     editing an existing filter or because the field only
 *     supports a single filter type.
 *  2. The filter type is unknown because the field supports
 *     multiple filter types.
 *
 * In the first case, this component will display the filter
 * configuration component directly.
 *
 * In the second case, this component will display the
 * appropriate filter type options for the user to choose from.
 * After selection, the component will then display the
 * configuration component.
 *
 * In addition, when the user applies or cancels, this component
 * is responsible for relaying the new filter configuration
 * back to the caller.
 */
@Component({
  standalone: true,
  imports: [
    CommonModule,
    DialogHeaderComponent,
    DragDropModule,
    FilterConfigurationComponent,
    FilterTypeSelectorComponent,
  ],
  selector: 'mode-filter-modal',
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterModalComponent implements OnInit {
  readonly FilterStepEnum = FilterStep;
  readonly formula: CompiledFormula;
  readonly view: FlamingoTypes.ModeView;
  readonly encoding: Encoding;
  readonly queryRunToken: string;
  readonly applyFilterFunc: (filter: EncFilterField, field: CompiledField) => void;
  readonly onCancel: () => void;

  filterStep$ = new BehaviorSubject<FilterStep>(FilterStep.SelectType);
  filterField: CompiledField;
  initialFilter: EncFilterField | undefined;
  readonly filterId: string;

  constructor(
    private dialogRef: MatLegacyDialogRef<FilterModalComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) private data: FilterModalData
  ) {
    this.formula = data.formula;
    this.initialFilter = data.initialFilter;
    this.filterId = data.initialFilter?.id ?? shortId();
    this.view = data.view;
    this.encoding = data.encoding;
    this.queryRunToken = data.queryRunToken;
    this.applyFilterFunc = data.applyFilter;
    this.onCancel = data.onCancel;

    // Base formula shouldn't be an agg/analytic form
    this.formula = this.formula.withNewFormula(
      this.formula.isAggregateable ? this.formula.formula : FormulaOperations.access(this.formula)
    );

    // The passed formula may be a CompiledAttr or a CompiledField
    // For a new filter, it will be a CompiledAttr.
    // When editing, it will be a CompiledField.
    // All of the child components of the Filter modal will expect
    // a CompiledField (ie filterField).
    // If we have a CompiledAttr, we will generate a CompiledField
    // from it based on the selected (or default) filter type that
    // is being created.
    this.filterField = this.formula.withNewFormula(this.formula.formula);

    // Convert ContinuousDate to RelativeDate
    if (this.initialFilter?.filterType === FilterType.ContinuousDate) {
      this.initialFilter = convertContinuousDateToRelativeDate(this.initialFilter, this.filterField);
      if (this.initialFilter.formula !== this.filterField.formula) {
        this.filterField = this.formula.withNewFormula(this.initialFilter.formula);
      }
    }

    // Normalize RelativeDate filters such that the filter formula w/y starts are in sync
    // with the filter, _falling back_ to the formula w/y starts when present
    if (this.initialFilter?.filterType === FilterType.RelativeDate) {
      this.initialFilter = relativeDateFilterSourcedByFilter(this.initialFilter, this.filterField);
      if (this.initialFilter.formula !== this.filterField.formula) {
        this.filterField = this.formula.withNewFormula(this.initialFilter.formula);
      }
    }
  }

  ngOnInit(): void {
    const filterStep = this.initFilterStep();
    this.filterStep$.next(filterStep);
  }

  initFilterStep() {
    let filterStep = FilterStep.SelectType;
    if (this.initialFilter !== undefined) {
      switch (this.initialFilter.filterType) {
        case FilterType.Discrete:
        case FilterType.DiscreteBoolean:
          filterStep = FilterStep.MultiValues;
          break;
        case FilterType.Continuous:
        case FilterType.ContinuousDate:
          filterStep = FilterStep.Range;
          break;
        case FilterType.TopByField:
          filterStep = FilterStep.TopByField;
          break;
        case FilterType.RelativeDate:
          filterStep = FilterStep.RelativeDateRange;
          break;
      }
    } else {
      switch (this.formula.formulaDataType ?? this.formula.attrDataType) {
        case DataType.Name.Boolean:
        case DataType.Name.String: {
          filterStep = FilterStep.MultiValues;
          break;
        }
        default: {
          if (this.formula.isNonScalarCalc) {
            filterStep = FilterStep.Range;
          }
          break;
        }
      }
    }
    return filterStep;
  }

  /**
   * Called when the user has selected the filter type and
   * clicked 'Next'.
   */
  setFilterType(filterType: FilterTypeDetail) {
    // Since the user had to pick a filter type, we know that
    // this is a new filter. We need to create a CompiledField
    // based on the 'formula' (eg CompiledAttr) plus the
    // filter type selection.
    this.makeFilterField(filterType);
    this.filterStep$.next(filterType.step);
  }

  makeFilterField(filterType: FilterTypeDetail) {
    const field = this.filterField;
    const attrName = escapeAttrName(field.attrName ?? '');
    switch (filterType.key) {
      case 'range': {
        let formula: string;
        if (field.isAttrDateType) {
          formula = FormulaOperations.dateTrunc(field, DateTime.Unit.Day);
        } else {
          formula = `[${attrName}]`;
        }

        this.filterField = CompiledField.fromFormula(this.view, formula, Attr.Type.Continuous);
        break;
      }
      case 'relative_date_range':
        this.filterField = makeRelativeDateFilterField(field);
        break;
      case 'individual_values':
        this.filterField = CompiledField.fromFormula(this.view, field.formula, Attr.Type.Discrete);
        break;
      case 'top_by_field':
        this.filterField = CompiledField.fromFormula(this.view, field.formula, Attr.Type.Discrete);
        break;
      case DatePart.YEAR:
      case DatePart.QUARTER:
      case DatePart.MONTH:
      case DatePart.DAY:
      case DatePart.WEEK:
      case DatePart.WEEKDAY:
      case DatePart.HOUR:
      case DatePart.MINUTE:
      case DatePart.SECOND: {
        const unit = DateTime.unit(filterType.key);
        if (unit) {
          const formula = FormulaOperations.datePart(field, unit);
          this.filterField = CompiledField.fromFormula(this.view, formula, Attr.Type.Discrete);
        }
        break;
      }
    }
  }

  updateFilterField(field: CompiledField) {
    this.filterField = field;
  }

  applyFilter(result: { filter: EncFilterField; close: boolean }) {
    const { filter, close } = result;

    // Call the callback
    const filterField = { ...filter, id: this.filterId };
    const field = CompiledField.fromEncField(this.view, filterField);
    this.applyFilterFunc(filterField, field);

    // Close the modal if requested
    if (close) {
      this.close();
    }
  }

  cancel() {
    // Call the cancel callback
    this.onCancel();
    this.close();
  }

  close() {
    this.dialogRef.close();
  }
}
